/**
 * It is easier for the iOS app to parse objects rather than primitive values.
 */
type MessageType = Record<string, any>

declare global {
    interface Window {
        webkit?: {
            messageHandlers: {
                [messageType: string]: {
                    postMessage: (message: string) => void;
                }
            }
        };
        AndroidJsBridge?: {
            sendMessage: (messageType: string, message: string) => void;
        };
    }
}

/**
 * This module provides low-level functions to send messages from web applications
 * to the webviews of native applications. Before using these, check if a method
 * on {@link MobileHooks} meets your needs.
 */
export class WebToNativeMessaging {

    /**
     * Send a message to the Dayforce app on iOS or Android if it is available.
     * The native application must be configured to accept these messages.
     * 
     * @remarks
     * This method is harmless to call in a web browser. It will do nothing.
     * 
     * Applications should not need to call this method directly.
     * Use the methods on {@link MobileHooks} instead since they have better types
     * and documentation.
     * 
     * @param messageType - the type of message. Each message type corresponds to a different block of code 
     * in the native application.
     * eg `web_application_loading_complete`, `show_loading_indicator`, `set_mobile_layout_configuration`
     *  
     * @param message - the actual data to send to the application eg.
     * - whether a native button should disabled or enabled
     * - a HTTP response body the native app cares about
     * - an empty object `{}` if there is no data to send
     * 
     * `message` will be `JSON.stringify`'d before being sent to the native application.
     */
    static sendMessage(
        messageType: string,
        message: MessageType
    ): void {
        if (window.webkit) {

            if (!window.webkit.messageHandlers[messageType]) {
                console.warn(`iOS app does not have a handler for '${messageType}'`)
                return;
            }
            window.webkit.messageHandlers[messageType].postMessage(JSON.stringify(message));
        }

        if (window.AndroidJsBridge) {
            window.AndroidJsBridge.sendMessage(
                messageType,
                JSON.stringify(message)
            );
        }
    }
};
